<template>
  <div class="flex min-h-screen flex-col bg-white">
    <Head :title="$t('booking.details_short')" />

    <DynamicBlock v-if="header" :block="header" />

    <main class="mx-auto w-full max-w-[1200px] flex-1 px-4 py-8 sm:px-6 lg:px-0">
      <!-- Breadcrumbs (Figma: Strona glowna / Szczegoly wizyty) -->
      <nav class="mb-8 flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link href="/" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="min-w-0 break-words text-[#0369a1]">{{ $t('booking.details_short') }}</span>
      </nav>

      <!-- Widok dostepny tylko, gdy wizyta jest w localStorage tej przegladarki. -->
      <section
        v-if="booking"
        class="mx-auto flex w-full max-w-[720px] flex-col items-center gap-6 py-8 text-center lg:py-12"
      >
        <!-- Status badge -->
        <span
          class="flex items-center justify-center gap-2 rounded-full px-3 py-1.5"
          :class="statusBadge.wrapClass"
        >
          <Icon v-if="statusBadge.icon" :name="statusBadge.icon" class="size-4 shrink-0" />
          <span class="text-sm font-medium leading-4">{{ statusBadge.label }}</span>
        </span>

        <!-- Title -->
        <div class="flex flex-col items-center text-[#262626]">
          <h1 class="text-[28px] font-medium leading-[36px] tracking-[-0.02em] lg:text-[36px] lg:leading-[44px]">
            {{ headingTitle }}
          </h1>
          <p
            v-if="dateLabel"
            class="text-[28px] font-medium leading-[36px] tracking-[-0.02em] lg:text-[36px] lg:leading-[44px]"
          >
            {{ dateLabel }}
          </p>
        </div>

        <!-- Info rows -->
        <div class="flex flex-col items-start gap-0">
          <div v-if="booking.service?.name || serviceMeta" class="flex items-center gap-2 py-3.5">
            <Icon name="briefcase" class="size-4 shrink-0 text-[#1f2937]" />
            <p class="text-left text-sm leading-4 text-[#111827]">
              <span v-if="booking.service?.name">{{ booking.service.name }}</span>
              <span v-if="serviceMeta" class="font-semibold text-[#16a34a]">{{ ' ' }}({{ serviceMeta }})</span>
            </p>
          </div>
          <div v-if="booking.specialistName" class="flex items-center gap-2 py-3.5">
            <Icon name="stethoscope" class="size-4 shrink-0 text-[#1f2937]" />
            <p class="text-left text-sm leading-4 text-[#111827]">{{ booking.specialistName }}</p>
          </div>
          <div v-if="facilityName" class="flex items-start gap-2 py-3.5">
            <Icon name="location" class="size-4 shrink-0 text-[#1f2937]" />
            <p class="text-left text-sm leading-4 text-[#111827]">
              {{ facilityName }}
              <span v-if="facilityAddress" class="text-[#6b7280]"> ({{ facilityAddress }})</span>
            </p>
          </div>
          <div v-if="patientName" class="flex items-center gap-2 py-3.5">
            <Icon name="user" class="size-4 shrink-0 text-[#1f2937]" />
            <p class="text-left text-sm leading-4 text-[#111827]">{{ patientName }}</p>
          </div>
        </div>

        <!-- Contextual action (pay / rebook) mirroring the list view -->
        <a
          v-if="canPay"
          :href="paymentHref"
          class="flex h-12 items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white no-underline drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)]"
          style="background-image: linear-gradient(160.691deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
        >
          {{ $t('booking.pay_visit') }}
        </a>
        <Link
          v-else-if="canRebook"
          :href="rebookHref"
          class="flex h-12 items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white no-underline drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)]"
          style="background-image: linear-gradient(160.691deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
        >
          {{ $t('booking.rebook') }}
        </Link>

        <!-- Cancel (Figma: czerwony link) -->
        <button
          v-if="canCancel"
          type="button"
          class="flex h-10 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-[#ef4444] drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)] transition-colors hover:bg-red-50 disabled:opacity-50"
          :disabled="canceling"
          @click="cancelVisit"
        >
          {{ canceling ? $t('booking.canceling') : $t('booking.cancel_visit') }}
        </button>
        <!-- Oplacona/zarejestrowana wizyta: anulowanie idzie przez link Medfile. -->
        <a
          v-else-if="canShowCancelUrl"
          :href="cancelUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="flex h-10 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-[#ef4444] no-underline drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)] transition-colors hover:bg-red-50"
        >
          {{ $t('booking.cancel_visit') }}
        </a>

        <!-- Map -->
        <iframe
          v-if="mapSrc"
          :src="mapSrc"
          class="h-[280px] w-full max-w-[584px] rounded-xl border-0 lg:h-[386px]"
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
          :title="facilityName || 'CODE Centrum Medyczne'"
        />

        <!-- Extra (poza designem): dyskretna, szara informacja inline. Gdy wizyta ma
             juz Medfile uuid - pokazujemy ID; jesli jeszcze nie - komunikat o braku. -->
        <p class="mt-2 text-xs leading-4 text-neutral-400">
          <template v-if="medfileId">{{ $t('booking.visit_medfile_id') }}: {{ medfileId }}</template>
          <template v-else>{{ $t('booking.visit_medfile_pending') }}</template>
        </p>
      </section>

      <!-- Brak dostepu / wizyta nie jest w tej przegladarce -->
      <section
        v-else
        class="mx-auto flex w-full max-w-[720px] flex-col items-center gap-4 py-16 text-center"
      >
        <h1 class="text-2xl font-medium tracking-[-0.02em] text-[#262626] lg:text-[32px] lg:leading-10">
          {{ $t('booking_details.not_found_title') }}
        </h1>
        <p class="text-base leading-6 text-neutral-600">{{ $t('booking_details.local_note') }}</p>
        <Link
          :href="visitsIndexHref"
          class="flex items-center gap-2 rounded-lg bg-[#149dca] px-5 py-3 text-base font-semibold leading-[18px] text-white no-underline transition-colors hover:bg-[#1189b1]"
        >
          {{ $t('booking.history_title') }}
        </Link>
      </section>
    </main>

    <DynamicBlock v-if="footer" :block="footer" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import DynamicBlock from '@/Shared/DynamicBlock.vue';
import Icon from '@/Shared/Icon.vue';
import { formatServiceMeta } from '@/utils/serviceMeta';
import { formatBookingDate } from '@/utils/bookingDate';
import { deriveVisitStatus, isCanceledVisitStatus } from '@/utils/visitStatus';
import { getBooking, saveBooking, mergeBookings } from '@/utils/bookingStorage';
import { subscribeToVisit, visitNeedsLiveUpdates } from '@/utils/visitCable';

export default {
  name: 'FrontofficeVisit',
  components: { Head, Link, DynamicBlock, Icon },
  layout: Layout,
  props: {
    bookingId: { type: String, default: null },
    header: { type: Object, default: null },
    footer: { type: Object, default: null },
  },
  data() {
    return {
      // Wizyta wpuszczana wylacznie z localStorage (patrz mounted) - brak
      // renderowania danych po samym id z URL.
      booking: null,
      canceling: false,
      refreshing: false,
      unsubscribeVisitCable: null,
      subscribedVisitId: null,
      paymentRedirectTimer: null,
    };
  },
  computed: {
    visitsIndexHref() {
      return '/wizyty';
    },
    status() {
      return this.booking ? deriveVisitStatus(this.booking) : null;
    },
    statusBadge() {
      const map = {
        paid: { label: this.$t('booking.status_paid'), wrapClass: 'bg-[#16a34a] text-[#f0fdf4]', icon: 'check' },
        registered: { label: this.$t('booking.status_registered'), wrapClass: 'bg-[#16a34a] text-[#f0fdf4]', icon: 'check' },
        pending_payment: { label: this.$t('booking.status_pending_payment'), wrapClass: 'bg-[#fff4e5] text-[#b36900]', icon: null },
        failed: { label: this.$t('booking.status_failed'), wrapClass: 'bg-[#fef2f2] text-[#ef4444]', icon: null },
        completed: { label: this.$t('booking.status_completed'), wrapClass: 'bg-[#e5e7eb] text-[#111827]', icon: null },
        payment_canceled: { label: this.$t('booking.status_canceled'), wrapClass: 'bg-[#6b7280] text-white', icon: null },
        canceled: { label: this.$t('booking.status_canceled'), wrapClass: 'bg-[#6b7280] text-white', icon: null },
      };
      return map[this.status] || map.pending_payment;
    },
    headingTitle() {
      const map = {
        paid: this.$t('booking.visit_title_booked'),
        registered: this.$t('booking.visit_title_booked'),
        pending_payment: this.$t('booking.visit_title_pending'),
        failed: this.$t('booking.visit_title_failed'),
        completed: this.$t('booking.visit_title_completed'),
        payment_canceled: this.$t('booking.visit_title_canceled'),
        canceled: this.$t('booking.visit_title_canceled'),
      };
      return map[this.status] || map.pending_payment;
    },
    serviceMeta() {
      return formatServiceMeta(this.booking?.service);
    },
    dateLabel() {
      return formatBookingDate(this.booking?.output?.visit?.start || this.booking?.chosenDate);
    },
    facilityName() {
      return this.booking?.facility?.name || '';
    },
    facilityAddress() {
      return this.booking?.facility?.address || '';
    },
    patientName() {
      const patient = this.booking?.patient;
      if (!patient) return '';
      return [patient.name, patient.lastname].filter(Boolean).join(' ');
    },
    medfileId() {
      return this.booking?.medfileVisitUuid || null;
    },
    mapSrc() {
      const query = [this.facilityName, this.facilityAddress].filter(Boolean).join(' ');
      if (!query) return null;
      return `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=16&output=embed`;
    },
    canPay() {
      if (this.status !== 'pending_payment') return false;
      return this.booking?.canPay === true || !!this.paymentHref;
    },
    paymentHref() {
      return this.booking?.output?.paymentUrl || this.booking?.payment?.redirect_uri || null;
    },
    canRebook() {
      return ['failed', 'completed', 'payment_canceled', 'canceled'].includes(this.status);
    },
    rebookHref() {
      const serviceSlug = this.booking?.service?.slug;
      if (serviceSlug) return `${this.$routes.service(serviceSlug)}#umow-wizyte`;
      if (!this.booking?.specialistSlug) return this.$routes.specialists_index();
      return `${this.$routes.specialist(this.booking.specialistSlug)}#umow-wizyte`;
    },
    canCancel() {
      if (['completed', 'failed'].includes(this.status) || isCanceledVisitStatus(this.status)) return false;
      if (this.booking?.canCancel === false) return false;
      return ['pending_payment', 'paid', 'registered'].includes(this.status);
    },
    cancelUrl() {
      return this.booking?.output?.cancelUrl || null;
    },
    // Hide Medfile cancel link for closed visits (completed / canceled / failed).
    canShowCancelUrl() {
      if (['completed', 'failed'].includes(this.status) || isCanceledVisitStatus(this.status)) return false;
      return !!this.cancelUrl;
    },
  },
  watch: {
    booking() {
      this.syncVisitCableSubscription();
    },
  },
  mounted() {
    // Gating: wpuszczamy tylko, gdy ta wizyta jest w localStorage (koszyk).
    this.booking = getBooking(this.bookingId);
    if (this.booking) this.refreshFromServer();
    this.syncVisitCableSubscription();
    this.maybeRedirectToPayment();

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.refreshFromServer);
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.refreshFromServer);
    }
    this.clearPaymentRedirectTimer();
    this.teardownVisitCableSubscription();
  },
  methods: {
    // After registration we land here with ?redirect_to_payment=1, show the
    // visit briefly, then send the patient to PayU. PayU continueUrl returns
    // to /wizyty/:id without that flag, so the callback stays on this view.
    maybeRedirectToPayment() {
      if (typeof window === 'undefined') return;
      if (!this.booking || !this.canPay || !this.paymentHref) return;

      const params = new URLSearchParams(window.location.search);
      if (params.get('redirect_to_payment') !== '1') return;

      params.delete('redirect_to_payment');
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', nextUrl);

      this.clearPaymentRedirectTimer();
      this.paymentRedirectTimer = window.setTimeout(() => {
        if (!this.canPay || !this.paymentHref) return;
        window.location.assign(this.paymentHref);
      }, 1200);
    },
    clearPaymentRedirectTimer() {
      if (this.paymentRedirectTimer != null) {
        window.clearTimeout(this.paymentRedirectTimer);
        this.paymentRedirectTimer = null;
      }
    },
    async refreshFromServer() {
      // Nigdy nie dociagamy danych, jesli wizyty nie ma w localStorage.
      if (!this.booking?.id || this.refreshing) return;

      this.refreshing = true;
      try {
        const params = new URLSearchParams({ ids: this.booking.id });
        const response = await fetch(`/api/wizyty?${params.toString()}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const data = await response.json();
        const fresh = Array.isArray(data.visits)
          ? data.visits.find((visit) => visit.id === this.booking.id)
          : null;
        if (fresh) {
          mergeBookings([fresh]);
          this.booking = getBooking(this.booking.id);
        }
      } catch {
        // Offline / blad API - zostawiamy lokalny cache bez zmian.
      } finally {
        this.refreshing = false;
      }
    },
    // Live status while waiting for PayU notify / Medfile register, so the view
    // flips to "paid" without a manual refresh once the webhook lands.
    syncVisitCableSubscription() {
      if (!visitNeedsLiveUpdates(this.booking)) {
        this.teardownVisitCableSubscription();
        return;
      }

      const visitId = String(this.booking.id);
      if (this.subscribedVisitId === visitId && this.unsubscribeVisitCable) return;

      this.teardownVisitCableSubscription();
      this.subscribedVisitId = visitId;
      this.unsubscribeVisitCable = subscribeToVisit(visitId, (visit) => {
        try {
          saveBooking(visit);
        } catch {
          // cache to tylko wygoda
        }
        this.booking = getBooking(visitId);
      });
    },
    teardownVisitCableSubscription() {
      this.unsubscribeVisitCable?.();
      this.unsubscribeVisitCable = null;
      this.subscribedVisitId = null;
    },
    async cancelVisit() {
      if (!this.booking?.id || this.canceling) return;
      if (!window.confirm(this.$t('booking.cancel_confirm'))) return;

      this.canceling = true;
      try {
        const response = await fetch(`/api/wizyty/${encodeURIComponent(this.booking.id)}/cancel`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
        const data = await response.json().catch(() => ({}));

        if (data.cancelUrl) {
          window.open(data.cancelUrl, '_blank', 'noopener,noreferrer');
        }

        if (data.visit) {
          this.booking = saveBooking(data.visit);
          return;
        }

        if (!response.ok) {
          window.alert(data.error || this.$t('booking.cancel_error'));
        }
      } catch {
        window.alert(this.$t('booking.cancel_error'));
      } finally {
        this.canceling = false;
      }
    },
  },
};
</script>
